import { useState } from 'react';
import { useUi } from '../i18n/uiTranslations';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Send } from 'lucide-react';
import { scrollToId } from '../lib/gsap';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { name: '', email: '', phone: '', position: '', linkedin: '', message: '' };

const benefits = [1, 2, 3, 4].map((n) => ({ title: `b${n}t`, description: `b${n}d` }));

const openPositions = [
  { title: 'p1', location: 'lRemote', department: 'dEngineering' },
  { title: 'p2', location: 'lRemote', department: 'dEngineering' },
  { title: 'p3', location: 'lRemote', department: 'dEngineering' },
  { title: 'p4', location: 'lRemote', department: 'dProduct' },
  { title: 'p5', location: 'lEurope', department: 'dSales' },
  { title: 'p6', location: 'lRemote', department: 'dSupport' },
];

const Field = ({ label, children }) => (
  <label className="block"><span className="field-label">{label}</span>{children}</label>
);

const CareersPage = () => {
  const [formData, setFormData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const u = useUi();
  const [brandBefore, brandAfter] = u('careersTitle').split('{brand}');
  const setF = (k) => (e) => setFormData((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.position) {
      toast.error(u('required'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/careers`, formData);
      toast.success(u('appSuccess'));
      setFormData(EMPTY);
    } catch (error) {
      toast.error(u('appFail'));
    } finally {
      setLoading(false);
    }
  };

  const apply = (title) => {
    setFormData((p) => ({ ...p, position: title }));
    scrollToId('apply-form');
  };

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="careers-page">
      <Header />

      <section className="px-6 lg:px-12 pt-36 lg:pt-48 pb-24 lg:pb-32" style={{ background: 'radial-gradient(60% 60% at 85% 20%, rgba(200,170,115,0.07), transparent 70%)' }}>
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow rise-in">{u('joinTeam')}</p>
            <h1 className="display-xl mt-8 text-[clamp(3.2rem,8vw,8rem)] rise-in" style={{ animationDelay: '0.1s' }}>
              {brandBefore}<span className="italic metal-text">SOFTMAYA</span>{brandAfter}
            </h1>
          </div>
          <p className="lg:col-span-4 text-base sm:text-lg leading-relaxed text-[#ABA59A] rise-in" style={{ animationDelay: '0.2s' }}>
            {u('careersDesc')}
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-12 pb-28 lg:pb-40">
        <div className="max-w-[1440px] mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl text-[#F4F0E8]">{u('whyWork')}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 border-t border-[#F4F0E8]/10">
            {benefits.map((b, i) => (
              <div key={b.title} className={`py-10 sm:pr-8 ${i ? 'lg:pl-8 lg:border-l' : ''} ${i % 2 ? 'sm:pl-8 sm:border-l' : ''} border-[#F4F0E8]/10 ${i > 0 ? 'border-t sm:border-t-0' : ''} ${i > 1 ? 'sm:border-t lg:border-t-0' : ''}`} data-testid={`career-benefit-${i}`}>
                <span className="font-mono-lux text-[0.7rem] text-[#C8AA73]">0{i + 1}</span>
                <h3 className="font-display text-2xl text-[#F4F0E8] mt-5">{u(b.title)}</h3>
                <p className="mt-3 text-[0.95rem] text-[#ABA59A] leading-relaxed">{u(b.description)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-28 lg:py-40 bg-[#0c0c0b] border-t border-[#F4F0E8]/[0.06]">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <h2 className="display-xl text-[clamp(2.4rem,4.6vw,4.4rem)]">{u('openPositions')}</h2>
          </div>
          <ul className="lg:col-span-8 border-t border-[#F4F0E8]/10">
            {openPositions.map((p, i) => (
              <li key={p.title} className="group border-b border-[#F4F0E8]/10" data-testid={`career-position-${i}`}>
                <button onClick={() => apply(u(p.title))} className="w-full text-left py-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <span>
                    <span className="block font-display text-2xl sm:text-[1.75rem] text-[#F4F0E8] group-hover:text-[#E8D6AF] transition-colors">{u(p.title)}</span>
                    <span className="mt-3 flex flex-wrap gap-x-5 gap-y-1 font-mono-lux text-[0.72rem] text-[#7C776E]">
                      <span>{u(p.department)}</span><span>{u(p.location)}</span><span>{u('fullTime')}</span>
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm text-[#C8AA73] shrink-0">
                    {u('applyNow')} <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="apply-form" className="px-6 lg:px-12 py-28 lg:py-40 border-t border-[#F4F0E8]/[0.06]">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <h2 className="display-xl text-[clamp(2.4rem,4.6vw,4.4rem)]">{u('applyNow')}</h2>
            <p className="mt-6 text-[#ABA59A] text-lg leading-relaxed max-w-[420px]">{u('applyDesc')}</p>
          </div>
          <form onSubmit={handleSubmit} className="lg:col-span-7 rounded-[28px] metal-edge bg-[#111110] p-6 sm:p-10 lg:p-14 space-y-6" data-testid="career-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label={u('fFullName')}><Input value={formData.name} onChange={setF('name')} className="input-dark" placeholder="John Doe" required data-testid="career-name" /></Field>
              <Field label={u('fEmail')}><Input type="email" value={formData.email} onChange={setF('email')} className="input-dark" placeholder="john@example.com" required data-testid="career-email" /></Field>
              <Field label={u('fPhone')}><Input value={formData.phone} onChange={setF('phone')} className="input-dark" placeholder="+1 234 567 890" data-testid="career-phone" /></Field>
              <Field label={u('fPosition')}><Input value={formData.position} onChange={setF('position')} className="input-dark" placeholder={u('phPosition')} required data-testid="career-position" /></Field>
            </div>
            <Field label={u('fLinkedin')}><Input value={formData.linkedin} onChange={setF('linkedin')} className="input-dark" placeholder="https://linkedin.com/in/yourprofile" data-testid="career-linkedin" /></Field>
            <Field label={u('fAbout')}>
              <Textarea value={formData.message} onChange={setF('message')} className="input-dark min-h-[150px] resize-none" placeholder={u('phAbout')} data-testid="career-message" />
            </Field>
            <button type="submit" className="btn-primary" disabled={loading} data-testid="career-submit">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {u('submitting')}</> : <><Send className="w-4 h-4" strokeWidth={1.5} /> {u('submitApp')}</>}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CareersPage;
